import { buildYZSeriesKey } from '../../../utils/chartSeriesKey';

/** Fraction of the value-axis span used for missing-series placeholder bars (thin tiles near zero). */
export const MISSING_BAR_PLACEHOLDER_RATIO = 0.004;

export type BarSeriesDatum = Record<string, unknown> & {
    value: number;
    metricName: string;
    metricLabel: string;
    configColor: string;
    axisGroup: 'y1' | 'y2';
    dataKey: string;
    dodgeGroup: string;
    isMissingPlaceholder?: boolean;
};

export function computeMissingBarPlaceholderValue(
    y1MinMax?: { min: number; max: number },
    y2MinMax?: { min: number; max: number },
): number {
    const extents = [y1MinMax, y2MinMax].filter(Boolean) as Array<{ min: number; max: number }>;
    if (extents.length === 0) return 1;
    const maxAbs = Math.max(...extents.flatMap((e) => [Math.abs(e.min), Math.abs(e.max)]));
    const sign = extents.every((e) => e.max <= 0) ? -1 : 1;
    return maxAbs > 0 ? sign * maxAbs * MISSING_BAR_PLACEHOLDER_RATIO : 1;
}

export type FillMissingBarSeriesPlaceholdersArgs = {
    categories: string[];
    categoryRecords: Array<Record<string, unknown>>;
    xAxisKey: string;
    y1BarConfigs: Array<{ dataKey: string; color: string; label?: string }>;
    y2BarConfigs: Array<{ dataKey: string; color: string; label?: string }>;
    uniqueZSeries: string[];
    seriesColorMap: Map<string, string>;
    placeholderValue: number;
};

/**
 * When a category has data for only some Z (or metric) series, G2 dodge still reserves band
 * space for every series — leaving empty gaps. Insert thin near-zero placeholder bars so each
 * category row stays visually filled without affecting axis domains or value labels.
 */
export function fillMissingBarSeriesPlaceholders(
    combinedData: BarSeriesDatum[],
    {
        categories,
        categoryRecords,
        xAxisKey,
        y1BarConfigs,
        y2BarConfigs,
        uniqueZSeries,
        seriesColorMap,
        placeholderValue,
    }: FillMissingBarSeriesPlaceholdersArgs,
): BarSeriesDatum[] {
    const totalSeriesSlots =
        (y1BarConfigs.length + y2BarConfigs.length) * Math.max(1, uniqueZSeries.length);
    if (totalSeriesSlots <= 1) return combinedData;

    const existingKeys = new Set(
        combinedData.map((d) => `${String(d[xAxisKey])}|${d.axisGroup}|${d.metricName}`),
    );

    const zSeriesToValues = new Map<string, string[]>();
    categoryRecords.forEach((item) => {
        const zKey = item.zSeriesKey as string | undefined;
        const zVals = item.zValues as string[] | undefined;
        if (zKey && zVals?.length) {
            zSeriesToValues.set(zKey, zVals);
        }
    });

    const zKeysToFill = uniqueZSeries.length > 0 ? uniqueZSeries : [null];
    const placeholders: BarSeriesDatum[] = [];

    const axisConfigs: Array<{
        configs: Array<{ dataKey: string; color: string; label?: string }>;
        axisGroup: 'y1' | 'y2';
    }> = [
        { configs: y1BarConfigs, axisGroup: 'y1' },
        { configs: y2BarConfigs, axisGroup: 'y2' },
    ];

    for (const category of categories) {
        const sampleRow =
            categoryRecords.find((row) => row[xAxisKey] === category) ??
            ({ [xAxisKey]: category } as Record<string, unknown>);

        for (const { configs, axisGroup } of axisConfigs) {
            if (configs.length === 0) continue;

            for (const config of configs) {
                const metricLabel = config.label || config.dataKey;

                for (const zSeriesKey of zKeysToFill) {
                    const zValues = zSeriesKey
                        ? zSeriesToValues.get(zSeriesKey) ?? [zSeriesKey]
                        : undefined;
                    const seriesKey = zValues?.length
                        ? buildYZSeriesKey(metricLabel, zValues)
                        : metricLabel;
                    const dedupeKey = `${category}|${axisGroup}|${seriesKey}`;
                    if (existingKeys.has(dedupeKey)) continue;

                    placeholders.push({
                        ...sampleRow,
                        [xAxisKey]: category,
                        value: placeholderValue,
                        metricName: seriesKey,
                        metricLabel,
                        configColor: seriesColorMap.get(seriesKey) || config.color || '#5B50D1',
                        axisGroup,
                        dataKey: config.dataKey,
                        dodgeGroup: `${category}_${axisGroup}`,
                        isMissingPlaceholder: true,
                        ...(zSeriesKey && zValues?.length
                            ? { zValues, zSeriesKey }
                            : {}),
                    });
                    existingKeys.add(dedupeKey);
                }
            }
        }
    }

    return placeholders.length > 0 ? [...combinedData, ...placeholders] : combinedData;
}
